/**
 * The journal's month calendar (JRNL-02).
 *
 * No calendar component existed anywhere in the app and there is no date-picker
 * dependency, so this is built from scratch: a month grid, previous/next month
 * controls, **week starting Monday**.
 *
 * Four day states, and the one precedence rule that matters is that **Today
 * beats Not logged** — the current date renders as Today even before anybody
 * has filed anything against it.
 *
 * *Not logged* is derived from **absence**, never from a row: a date with no
 * notes is a date §6.2 did not return, and no placeholder is written for it
 * anywhere (`data-model/0004` §1 — *"It writes a fact nobody observed into a
 * document that gets signed off."*).
 *
 * **Nothing in here is absolutely positioned, and there is no `sr-only`.** Day
 * cells are the classic source of escapees, `sr-only` is `position: absolute`
 * by definition, and an escapee under the shell's scroll column extends the
 * *page's* scroll height rather than the column's (`architecture/0001`). Cell
 * names are carried by `aria-label`, which costs no element at all. The root is
 * `relative` as a second line of defence: it contains anything a future edit
 * adds.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { JournalDaySummary } from "@gb/contracts";
import { addDays, DISPLAY_LOCALE } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Today as the site sees it — a local `YYYY-MM-DD`, not a UTC instant.
 *
 * Exported because the queue and the composer need the same "today" this grid
 * paints, and a second definition that disagrees by a timezone would put the
 * Today fill on one date and the gap list on another.
 */
export function todayIso(): string {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");
}

/**
 * The first date the window covers.
 *
 * `project.startDate` is optional at setup and nothing enforces it, so null is
 * ordinary. It falls back to the earliest recorded date and then to today —
 * **never to an arbitrary lookback**, because an orange *Not logged* outline on
 * a day before the site existed is a gap that was never a gap.
 *
 * Exported so the calendar and the catch-up queue cannot answer it differently.
 */
export function resolveWindowStart(
  windowStart: string | null,
  days: readonly JournalDaySummary[],
): string {
  if (windowStart) return windowStart;
  let earliest: string | null = null;
  for (const day of days) {
    if (earliest === null || day.date < earliest) earliest = day.date;
  }
  return earliest ?? todayIso();
}

/**
 * Which dates count as logged.
 *
 * `noteCount === 0` rather than "absent from the response": an absent date has
 * no count and reads as 0 here, and a date that *is* present because somebody
 * dropped photographs on it still has no notes. Both are gaps, and the state is
 * called *Not logged* for exactly that reason.
 */
function noteCountsByDate(
  days: readonly JournalDaySummary[],
): Map<string, number> {
  return new Map(days.map((day) => [day.date, day.noteCount]));
}

const WEEKDAYS = [
  { short: "Mon", long: "Monday" },
  { short: "Tue", long: "Tuesday" },
  { short: "Wed", long: "Wednesday" },
  { short: "Thu", long: "Thursday" },
  { short: "Fri", long: "Friday" },
  { short: "Sat", long: "Saturday" },
  { short: "Sun", long: "Sunday" },
] as const;

function utc(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

/** `"2026-06-17"` → `"2026-06-01"`. */
function startOfMonth(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

function addMonths(monthStart: string, n: number): string {
  const d = utc(monthStart);
  d.setUTCMonth(d.getUTCMonth() + n);
  return startOfMonth(d.toISOString().slice(0, 10));
}

function daysInMonth(monthStart: string): number {
  const d = utc(monthStart);
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0),
  ).getUTCDate();
}

/** 0 = Monday … 6 = Sunday. The design's week starts on Monday. */
function mondayIndex(iso: string): number {
  return (utc(iso).getUTCDay() + 6) % 7;
}

function monthLabel(monthStart: string): string {
  return utc(monthStart).toLocaleDateString(DISPLAY_LOCALE, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** `"Monday 1 June 2026"` — the weekday matters on a site record. */
function dayLabel(iso: string): string {
  return utc(iso).toLocaleDateString(DISPLAY_LOCALE, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** The month's dates, padded to whole Monday-first weeks with nulls. */
function weeksOf(monthStart: string): (string | null)[][] {
  const total = daysInMonth(monthStart);
  const lead = mondayIndex(monthStart);
  const cells: (string | null)[] = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let day = 1; day <= total; day++) {
    cells.push(`${monthStart.slice(0, 8)}${String(day).padStart(2, "0")}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

type DayState = {
  today: boolean;
  notLogged: boolean;
  viewing: boolean;
  outOfWindow: boolean;
  noteCount: number;
};

export function JournalCalendar({
  days,
  windowStart,
  selected,
  onSelect,
}: {
  days: JournalDaySummary[];
  windowStart: string | null;
  selected: { from: string; to: string };
  onSelect: (range: { from: string; to: string }) => void;
}) {
  const today = todayIso();
  const [month, setMonth] = useState(() =>
    startOfMonth(selected.from || today),
  );

  /**
   * The first click of a pending range.
   *
   * Flight-picker behaviour: the first click sets the start, the second sets
   * the end, and a third starts over — which falls out of clearing the anchor
   * on the second click.
   */
  const [anchor, setAnchor] = useState<string | null>(null);

  // Roving tabindex. One day in the grid is a tab stop; the arrows move it.
  const [focused, setFocused] = useState(() =>
    startOfMonth(selected.from || today) === month
      ? selected.from || today
      : month,
  );
  // Only steal focus when the *keyboard* moved it. Focusing on mount would drag
  // the page to the rail every time the screen renders.
  const moveFocus = useRef(false);
  const gridRef = useRef<HTMLTableElement>(null);

  useEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    const cell = gridRef.current?.querySelector<HTMLButtonElement>(
      `[data-date="${focused}"]`,
    );
    cell?.focus();
  }, [focused]);

  const from = resolveWindowStart(windowStart, days);
  const counts = noteCountsByDate(days);

  const stateOf = (iso: string): DayState => {
    const noteCount = counts.get(iso) ?? 0;
    return {
      today: iso === today,
      // A past day inside the window with no notes at all. Today is excluded
      // here rather than only at paint time: a day still being worked is not a
      // day somebody failed to write up.
      notLogged: iso < today && iso >= from && noteCount === 0,
      viewing: iso >= selected.from && iso <= selected.to,
      outOfWindow: iso > today || iso < from,
      noteCount,
    };
  };

  const pick = (iso: string): void => {
    if (anchor === null) {
      setAnchor(iso);
      onSelect({ from: iso, to: iso });
      return;
    }
    setAnchor(null);
    onSelect(
      anchor <= iso ? { from: anchor, to: iso } : { from: iso, to: anchor },
    );
  };

  const moveTo = (iso: string): void => {
    moveFocus.current = true;
    setFocused(iso);
    if (startOfMonth(iso) !== month) setMonth(startOfMonth(iso));
  };

  const onKeyDown = (event: KeyboardEvent, iso: string): void => {
    const step: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    if (event.key in step) {
      event.preventDefault();
      moveTo(addDays(iso, step[event.key]!));
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      moveTo(addDays(iso, -mondayIndex(iso)));
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      moveTo(addDays(iso, 6 - mondayIndex(iso)));
    }
  };

  const goMonth = (n: number): void => {
    const next = addMonths(month, n);
    setMonth(next);
    setFocused(next);
  };

  const weeks = weeksOf(month);

  return (
    <section
      // `relative` is not styling: it makes this subtree a containing block, so
      // anything absolutely positioned added here later is clipped by the
      // shell's scroll column rather than extending the page. `architecture/0001`.
      className="relative rounded-card-lg border-[1.5px] border-border bg-surface p-4 shadow-flat"
      aria-label="Journal calendar"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => goMonth(-1)}
          className="flex size-8 cursor-pointer items-center justify-center rounded-control border-[1.5px] border-border bg-surface text-ink-slate transition-colors hover:border-border-hover hover:bg-subtle"
        >
          <ChevronLeft size={16} />
        </button>
        <span
          aria-live="polite"
          className="font-display text-[0.86rem] font-semibold tracking-[0.06em] text-ink uppercase"
        >
          {monthLabel(month)}
        </span>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => goMonth(1)}
          className="flex size-8 cursor-pointer items-center justify-center rounded-control border-[1.5px] border-border bg-surface text-ink-slate transition-colors hover:border-border-hover hover:bg-subtle"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <table
        ref={gridRef}
        role="grid"
        aria-label={`${monthLabel(month)} — pick a day, or a second day for a range`}
        className="w-full table-fixed border-collapse"
      >
        <thead>
          <tr>
            {WEEKDAYS.map((weekday) => (
              <th
                key={weekday.short}
                scope="col"
                abbr={weekday.long}
                className="pb-1.5 font-display text-[0.62rem] font-semibold tracking-[0.08em] text-muted uppercase"
              >
                {weekday.short}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week.find(Boolean) ?? String(week.length)}>
              {week.map((iso, index) => {
                if (iso === null)
                  return <td key={`pad-${index}`} className="p-[2px]" />;
                const state = stateOf(iso);
                return (
                  <td
                    key={iso}
                    className="p-[2px]"
                    // Announced without needing a hidden element, and it is the
                    // only signal a Today cell inside the range can carry —
                    // the dark fill has already won the paint.
                    aria-selected={state.viewing || undefined}
                  >
                    <button
                      type="button"
                      data-date={iso}
                      tabIndex={iso === focused ? 0 : -1}
                      aria-label={accessibleName(iso, state)}
                      aria-current={state.today ? "date" : undefined}
                      onClick={() => {
                        setFocused(iso);
                        pick(iso);
                      }}
                      onKeyDown={(event) => onKeyDown(event, iso)}
                      className={cn(
                        "flex h-9 w-full cursor-pointer items-center justify-center",
                        "rounded-control border-[1.5px] border-transparent",
                        "font-mono text-[0.8rem] transition-colors",
                        "text-ink-slate hover:bg-subtle-2",
                        state.outOfWindow && "text-faint",
                        // Orange outline, and only ever on a past day inside
                        // the window that holds no notes.
                        state.notLogged && "border-brand text-brand",
                        state.viewing && "bg-subtle text-ink",
                        // Last, and unconditional: Today beats Not logged.
                        state.today &&
                          "border-transparent bg-chrome text-white hover:bg-ink-soft",
                      )}
                    >
                      {Number(iso.slice(8))}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* The three active states, named. The fourth is the absence of all
          three and needs no key. */}
      <ul className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <LegendKey swatch="bg-chrome" label="Today" />
        <LegendKey
          swatch="border-[1.5px] border-brand bg-surface"
          label="Not logged"
        />
        <LegendKey swatch="bg-subtle" label="Viewing" />
      </ul>
    </section>
  );
}

function LegendKey({ swatch, label }: { swatch: string; label: string }) {
  return (
    <li className="flex items-center gap-1.5">
      <span aria-hidden className={cn("size-3 rounded-[4px]", swatch)} />
      <span className="text-[0.75rem] text-muted">{label}</span>
    </li>
  );
}

/** `"Monday 1 June 2026, today, 2 notes"`. */
function accessibleName(iso: string, state: DayState): string {
  const parts: string[] = [];
  if (state.today) parts.push("today");
  else if (state.outOfWindow) parts.push("outside the window");
  else if (state.notLogged) parts.push("not logged");
  if (state.noteCount > 0) {
    parts.push(
      `${state.noteCount} ${state.noteCount === 1 ? "note" : "notes"}`,
    );
  }
  if (state.viewing) parts.push("in the range being viewed");
  return [dayLabel(iso), ...parts].join(", ");
}
